import { useCallback, useEffect, useState } from 'react';
import {
  orderopsApi,
  type OrderExceptionRecord,
  type OrderHealthRecord,
  type OrderItemRecord,
  type OrderOpsDashboard,
  type OrderRecommendation,
  type OrderRecord,
  type ShipmentRecord,
  type SyncJobRecord,
  type TimelineEntry,
  type TrackingEventRecord,
} from '../../api/orderops';

type Tab = 'dashboard' | 'explorer' | 'detail' | 'shipments' | 'tracking' | 'exceptions' | 'recommendations';

function statusClass(s: string) {
  if (s === 'delivered') return 'text-emerald-700 bg-emerald-50';
  if (s === 'shipped' || s === 'in_transit') return 'text-sky-700 bg-sky-50';
  if (s === 'unshipped' || s === 'pending') return 'text-amber-700 bg-amber-50';
  if (s === 'partially_shipped') return 'text-orange-700 bg-orange-50';
  if (s === 'cancelled' || s === 'returned' || s === 'failed') return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

function healthClass(score: number) {
  if (score >= 80) return 'text-emerald-700';
  if (score >= 60) return 'text-amber-700';
  return 'text-red-700';
}

function severityClass(s?: string) {
  if (s === 'critical') return 'text-red-700 bg-red-50';
  if (s === 'high') return 'text-orange-700 bg-orange-50';
  if (s === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

function channelLabel(c: string) {
  return c === 'fba' ? 'FBA' : c === 'mfn' ? 'MFN' : c.toUpperCase();
}

export function OrderOpsPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<OrderOpsDashboard | null>(null);
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItemRecord[]>([]);
  const [shipments, setShipments] = useState<ShipmentRecord[]>([]);
  const [tracking, setTracking] = useState<TrackingEventRecord[]>([]);
  const [health, setHealth] = useState<OrderHealthRecord[]>([]);
  const [exceptions, setExceptions] = useState<OrderExceptionRecord[]>([]);
  const [recommendations, setRecommendations] = useState<OrderRecommendation[]>([]);
  const [syncJobs, setSyncJobs] = useState<SyncJobRecord[]>([]);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, ords, shps, trk, hlth, exc, recs, sync, tl] = await Promise.all([
        orderopsApi.dashboard(),
        orderopsApi.listOrders(),
        orderopsApi.listShipments(),
        orderopsApi.listTracking(),
        orderopsApi.listHealth(),
        orderopsApi.listExceptions(),
        orderopsApi.listRecommendations(),
        orderopsApi.listSyncJobs(),
        orderopsApi.listTimeline(),
      ]);
      setDashboard(dash);
      setOrders(ords);
      setShipments(shps);
      setTracking(trk);
      setHealth(hlth);
      setExceptions(exc);
      setRecommendations(recs);
      setSyncJobs(sync);
      setTimeline(tl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load order operations data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const selectOrder = async (order: OrderRecord) => {
    setSelectedOrder(order);
    try {
      const items = await orderopsApi.listOrderItems(order.id);
      const tl = await orderopsApi.listTimeline(order.id);
      setOrderItems(items);
      setTimeline(tl);
    } catch {
      setOrderItems([]);
    }
  };

  const generateRecs = async () => {
    setBusy(true);
    try {
      await orderopsApi.generateRecommendations();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Order Dashboard' },
    { id: 'explorer', label: 'Order Explorer' },
    { id: 'detail', label: 'Order Detail' },
    { id: 'shipments', label: 'Shipment Center' },
    { id: 'tracking', label: 'Tracking Timeline' },
    { id: 'exceptions', label: 'Exception Center' },
    { id: 'recommendations', label: 'AI Recommendations' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Orders &amp; Fulfillment Operations</h1>
          <p className="text-sm text-slate-500">
            Order lifecycle, shipment tracking, fulfillment intelligence &amp; AI copilot — APP-00002
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: "Today's Orders", value: dashboard.today_orders.toString() },
              { label: 'Pending', value: dashboard.pending_orders.toString() },
              { label: 'Late Shipments', value: dashboard.late_shipments.toString() },
              { label: 'Delayed Deliveries', value: dashboard.delayed_deliveries.toString() },
              { label: 'Cancelled', value: dashboard.cancelled_orders.toString() },
              { label: 'Avg Health Score', value: dashboard.avg_health_score.toFixed(0) },
              { label: 'Operational SLA', value: `${dashboard.operational_sla_pct.toFixed(1)}%` },
              { label: 'Active Exceptions', value: dashboard.active_exceptions.toString() },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">Sync Jobs</h3>
              <div className="mt-3 space-y-2">
                {syncJobs.map((j) => (
                  <div key={j.id} className="flex items-center justify-between text-sm">
                    <span>{j.mode} sync</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(j.status === 'completed' ? 'delivered' : j.status === 'running' ? 'shipped' : 'pending')}`}>
                      {j.status} · {j.orders_synced} orders
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">Top Health Issues</h3>
              <div className="mt-3 space-y-2">
                {health.slice(0, 3).map((h) => (
                  <div key={h.id} className="text-sm">
                    <span className="font-mono text-xs">{h.amazon_order_id}</span>
                    <p className="text-slate-600">{h.issue}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'explorer' && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Order ID</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Channel</th>
                <th className="px-4 py-2">Total</th>
                <th className="px-4 py-2">Health</th>
                <th className="px-4 py-2">Risk</th>
                <th className="px-4 py-2">Flags</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                  onClick={() => { selectOrder(o); setTab('detail'); }}
                >
                  <td className="px-4 py-2 font-mono text-xs">{o.amazon_order_id}</td>
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(o.status)}`}>
                      {o.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-2">{channelLabel(o.fulfillment_channel)}</td>
                  <td className="px-4 py-2">${o.order_total.toFixed(2)}</td>
                  <td className={`px-4 py-2 font-medium ${healthClass(o.health_score)}`}>{o.health_score.toFixed(0)}</td>
                  <td className={`px-4 py-2 ${healthClass(100 - o.delivery_risk_score)}`}>{o.delivery_risk_score.toFixed(0)}</td>
                  <td className="px-4 py-2 text-xs">
                    {o.is_prime && <span className="mr-1 rounded bg-amber-50 px-1 text-amber-700">Prime</span>}
                    {o.is_business_order && <span className="rounded bg-sky-50 px-1 text-sky-700">B2B</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'detail' && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
            {orders.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => selectOrder(o)}
                className={`w-full rounded-lg border p-3 text-left text-sm ${
                  selectedOrder?.id === o.id ? 'border-sky-300 bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                }`}
              >
                <p className="font-mono text-xs font-medium">{o.amazon_order_id}</p>
                <p className="text-slate-500">${o.order_total.toFixed(2)} · {o.status.replace(/_/g, ' ')}</p>
              </button>
            ))}
          </div>
          {selectedOrder && (
            <div className="lg:col-span-2 space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-mono text-lg font-medium">{selectedOrder.amazon_order_id}</h2>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(selectedOrder.status)}`}>
                    {selectedOrder.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
                  <div><span className="text-slate-500">Fulfillment:</span> {channelLabel(selectedOrder.fulfillment_channel)}</div>
                  <div><span className="text-slate-500">Total:</span> ${selectedOrder.order_total.toFixed(2)} {selectedOrder.currency}</div>
                  <div><span className="text-slate-500">Health:</span> <span className={healthClass(selectedOrder.health_score)}>{selectedOrder.health_score.toFixed(0)}</span></div>
                  <div><span className="text-slate-500">Delivery Risk:</span> {selectedOrder.delivery_risk_score.toFixed(0)}</div>
                  <div><span className="text-slate-500">Items:</span> {selectedOrder.item_count}</div>
                  <div><span className="text-slate-500">Marketplace:</span> {selectedOrder.marketplace}</div>
                </div>
              </div>

              {orderItems.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h3 className="font-medium text-slate-900">Order Items</h3>
                  <div className="mt-3 space-y-2">
                    {orderItems.map((i) => (
                      <div key={i.id} className="flex justify-between text-sm border-b border-slate-100 pb-2">
                        <div>
                          <p className="font-medium">{i.sku}</p>
                          <p className="text-xs text-slate-500">{i.title}</p>
                        </div>
                        <div className="text-right text-xs">
                          <p>Ordered: {i.quantity_ordered}</p>
                          <p>Shipped: {i.quantity_shipped}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {timeline.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h3 className="font-medium text-slate-900">Lifecycle Timeline</h3>
                  <div className="mt-3 space-y-3 border-l-2 border-sky-200 pl-4">
                    {timeline.map((t) => (
                      <div key={t.id} className="relative">
                        <div className="absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-sky-500" />
                        <p className="text-sm font-medium">{t.event.replace(/_/g, ' ')}</p>
                        {t.message && <p className="text-xs text-slate-500">{t.message}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'shipments' && (
        <div className="space-y-3">
          {shipments.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-mono text-sm">{s.amazon_order_id}</p>
                  <p className="text-sm text-slate-600">{s.carrier} · {s.tracking_number}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(s.status)}`}>
                  {s.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="mt-2 flex gap-4 text-xs text-slate-500">
                <span>{s.shipping_method}</span>
                {s.transit_days != null && <span>{s.transit_days}d transit</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'tracking' && (
        <div className="space-y-3">
          {tracking.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs">{t.tracking_number}</span>
                <span className="text-sm font-medium">{t.event}</span>
              </div>
              {t.location && <p className="mt-1 text-xs text-slate-500">{t.location}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'exceptions' && (
        <div className="space-y-3">
          {exceptions.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{e.amazon_order_id}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(e.severity)}`}>
                  {e.severity}
                </span>
                <span className="text-xs text-slate-400">{e.type.replace(/_/g, ' ')}</span>
              </div>
              {e.description && <p className="mt-1 text-sm text-slate-600">{e.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={generateRecs}
              disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                  #{r.priority_rank}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                {r.amazon_order_id && <span>Order: {r.amazon_order_id}</span>}
                <span>{r.type.replace(/_/g, ' ')}</span>
                <span>Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.expected_impact && <span>Impact: {r.expected_impact}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
